import { Dispatch, FC, SetStateAction } from "react";
import { IInputComponentProps, Input } from "../../Elements/Input";
import { useTranslation } from "react-i18next";
import {
  DateInput,
  deleteProps,
  FontAwesomeIcon,
  TextInput,
  Tooltip,
} from "@inceptionbg/ui-components";
import { Dialog } from "../../Elements/Dialog";
import {
  getActivitiesOptions,
  getActivityGroupsOptions,
  getLaborTypeGroupsOptions,
  getLaborTypesOptions,
  getTreatmentAgentsOptions,
  getLandCultureOptions,
} from "../../utils/LoadOptions";
import { SelectAsyncPaginate } from "../../Components/Inputs/SelectAsyncPaginate";
import { EntryTypes, IEntryType, IFieldEntry } from "../../types/IFieldBook";
import { Select } from "../../Components/Inputs/Select";
import { faCircleInfo } from "../../assets/icons/solid/faCircleInfo";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data?: Partial<IFieldEntry>;
  setData: Dispatch<SetStateAction<Partial<IFieldEntry>>>;
  entryFieldBookUuid: string;
  onSubmit: () => void;
  onDelete?: (uuid: string) => void;
}
export const FieldBookEntryDialog: FC<Props> = ({
  isOpen,
  onClose,
  data,
  setData,
  entryFieldBookUuid,
  onSubmit,
  onDelete,
}) => {
  const { t } = useTranslation();

  const allFields: (IInputComponentProps & { visibleFor?: IEntryType[] })[] = [
    {
      label: t("Type"),
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("EntryFormTypeTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <Select
            required
            value={
              data?.type ? { label: t(data.type), value: data.type } : null
            }
            options={EntryTypes.map((e) => ({ label: t(e), value: e }))}
            onChange={(e) =>
              setData({
                type: e.value as IEntryType,
                activity: undefined,
                laborType: undefined,
                treatmentAgent: undefined,
                treatmentAgentType: undefined,
                description: undefined,
                expense: undefined,
                quantity: undefined,
                income: undefined,
                unitOfMeasure: undefined,
              })
            }
          />
        </div>
      ),
    },
    {
      label: t("PlantCultureC"),
      visibleFor: [
        "ACTIVITY",
        "PROTECTION_AND_NUTRITION",
        "FUEL_CONSUMPTION",
        "DIRECT_COSTS",
        "YIELD",
        "ANOTHER_INCOME_ENTRY",
      ],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("EntryFormPlantCultureTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <SelectAsyncPaginate
            required
            value={
              data?.plantCulture
                ? {
                    value: data.plantCulture.uuid,
                    label: data.plantCulture
                      ? data.plantCulture.code + " - " + data.plantCulture.name
                      : "",
                  }
                : null
            }
            loadOptions={(
              search: string,
              loadedOptions: [],
              offset: { page: number }
            ) =>
              getLandCultureOptions(search, loadedOptions, offset, {
                fieldUuid: entryFieldBookUuid,
              })
            }
            onChange={(e) => {
              setData(
                e
                  ? {
                      ...data,
                      plantCulture: {
                        uuid: e.value,
                        code: e.code,
                        name: e.name,
                      },
                    }
                  : deleteProps(data, ["plantCulture"])
              );
            }}
          />
        </div>
      ),
    },
    {
      label: t(
        ["DIRECT_COSTS", "YIELD", "ANOTHER_INCOME_ENTRY"].includes(
          data?.type || ""
        )
          ? "Date"
          : "ActivityDate"
      ),
      visibleFor: [
        "ACTIVITY",
        "PROTECTION_AND_NUTRITION",
        "FUEL_CONSUMPTION",
        "DIRECT_COSTS",
        "YIELD",
        "ANOTHER_INCOME_ENTRY",
      ],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("ActivityDateTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <DateInput
            required
            date={data?.activityDate || ""}
            setDate={(e) => setData({ ...data, activityDate: e })}
          />
        </div>
      ),
    },

    {
      label: t("ActivityGroup"),
      visibleFor: [
        "ACTIVITY",
        "PROTECTION_AND_NUTRITION",
        "FUEL_CONSUMPTION",
        "DIRECT_COSTS",
      ],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("EntryFormActivityGroupTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <SelectAsyncPaginate
            required
            value={
              data?.activityGroup
                ? {
                    value: data.activityGroup.uuid,
                    label: data.activityGroup.name,
                  }
                : null
            }
            loadOptions={(search: string) =>
              getActivityGroupsOptions(search, {
                activityCodeStartsWithNumbers:
                  data?.type === "PROTECTION_AND_NUTRITION"
                    ? [3, 4]
                    : undefined,
              })
            }
            onChange={(e) =>
              setData({
                ...data,
                activityGroup: e
                  ? { uuid: e.value, name: e.label }
                  : deleteProps(data, ["activityGroup"]),
                activity: undefined,
                treatmentAgent: undefined,
                unitOfMeasure: undefined,
                quantity: undefined,
                description: undefined,
              })
            }
          />
        </div>
      ),
    },

    {
      label: t("Activity"),
      visibleFor: [
        "ACTIVITY",
        "PROTECTION_AND_NUTRITION",
        "FUEL_CONSUMPTION",
        "DIRECT_COSTS",
      ],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("EntryFormActivityTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <SelectAsyncPaginate
            required
            disabled={!data?.activityGroup?.uuid}
            value={
              data?.activity?.uuid
                ? {
                    value: data.activity.uuid,
                    label: data.activity.code + " - " + data.activity.name,
                  }
                : null
            }
            loadOptions={(search: string) =>
              getActivitiesOptions(search, [], {
                activityGroupUuid: data?.activityGroup?.uuid,
              })
            }
            onChange={(e) =>
              setData({
                ...data,
                activity: e
                  ? {
                      ...data?.activity,
                      uuid: e.value,
                      name: e.name,
                      code: e.code,
                    }
                  : deleteProps(data, ["activity"]),
                treatmentAgent: undefined,
                unitOfMeasure: undefined,
                quantity: undefined,
                description: undefined,
              })
            }
            refresh={[data?.activityGroup]}
          />
        </div>
      ),
    },

    {
      label: t("LaborTypeGroup"),
      visibleFor: ["ACTIVITY"],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("WorkforceTypeGroupTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <SelectAsyncPaginate
            required
            value={
              data?.laborTypeGroup
                ? {
                    value: data.laborTypeGroup.uuid,
                    label: data.laborTypeGroup.name,
                  }
                : null
            }
            loadOptions={getLaborTypeGroupsOptions}
            onChange={(e) =>
              setData({
                ...data,
                laborTypeGroup: e
                  ? { uuid: e.value, name: e.label }
                  : deleteProps(data, ["laborTypeGroup"]),
              })
            }
          />
        </div>
      ),
    },

    {
      label: t("LaborType"),
      visibleFor: ["ACTIVITY"],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("WorkforceTypeTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <SelectAsyncPaginate
            required
            disabled={!data?.laborTypeGroup?.uuid}
            value={
              data?.laborType?.uuid
                ? {
                    value: data.laborType.uuid,
                    label: data.laborType.code + " - " + data.laborType.name,
                  }
                : null
            }
            loadOptions={(search: string) =>
              getLaborTypesOptions(search, [], {
                laborTypeGroupUuid: data?.laborTypeGroup?.uuid,
              })
            }
            onChange={(e) =>
              setData({
                ...data,
                laborType: e
                  ? {
                      ...data?.laborType,
                      uuid: e.value,
                      name: e.name,
                      code: e.code,
                    }
                  : deleteProps(data, ["laborType"]),
              })
            }
            refresh={[data?.laborTypeGroup]}
          />
        </div>
      ),
    },

    {
      label: t("LaborHours"),
      visibleFor: ["ACTIVITY"],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("WorkingHoursAmountTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <TextInput
            required
            value={data?.quantity}
            setValue={(e) => setData({ ...data, quantity: e })}
            inputProps={{
              type: "number",
              min: 0,
              max: 24,
              onClick: (e) => e.currentTarget.select(),
            }}
          />
        </div>
      ),
    },

    {
      label: t("TreatmentAgent"),
      visibleFor: ["PROTECTION_AND_NUTRITION"],
      input: (
        <SelectAsyncPaginate
          required
          disabled={!data?.activity?.uuid}
          value={
            data?.treatmentAgent?.uuid
              ? {
                  value: data.treatmentAgent.uuid,
                  label: data.treatmentAgent.code
                    ? data.treatmentAgent.code +
                      " - " +
                      (data.treatmentAgent.name || "")
                    : data.treatmentAgent.name || "",
                }
              : null
          }
          loadOptions={(search: string) =>
            getTreatmentAgentsOptions(search, {
              activityUuid: data?.activity?.uuid,
            })
          }
          onChange={(e) =>
            setData({
              ...data,
              treatmentAgent: e
                ? {
                    uuid: e.value,
                    name: e.name,
                    code: e.code,
                    unit: e.unit,
                  }
                : deleteProps(data, ["treatmentAgent"]),
              unitOfMeasure: e?.unit,
              quantity: undefined,
            })
          }
          refresh={[data?.activity]}
        />
      ),
    },

    {
      label: t("QuantityValueC"),
      visibleFor: ["PROTECTION_AND_NUTRITION"],
      input: (
        <div className="flex center full-width">
          <TextInput
            required
            value={data?.quantity || ""}
            setValue={(e) => setData({ ...data, quantity: e })}
          />
          {data?.unitOfMeasure && (
            <span className="ml-2">{data.unitOfMeasure}</span>
          )}
        </div>
      ),
    },

    {
      label: t("Liter"),
      visibleFor: ["FUEL_CONSUMPTION"],
      input: (
        <TextInput
          required
          value={data?.quantity || ""}
          setValue={(e) => setData({ ...data, quantity: e })}
        />
      ),
    },

    {
      label: t("Description"),
      visibleFor: ["DIRECT_COSTS"],
      input: (
        <TextInput
          value={data?.description || ""}
          setValue={(e) => setData({ ...data, description: e })}
        />
      ),
    },

    {
      label: t("TotalCostDin"),
      visibleFor: [
        "ACTIVITY",
        "PROTECTION_AND_NUTRITION",
        "FUEL_CONSUMPTION",
        "DIRECT_COSTS",
      ],
      input: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("TotalExpenseTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <TextInput
            required
            value={data?.expense as string}
            setValue={(e) => setData({ ...data, expense: e })}
          />
        </div>
      ),
    },

    {
      label: t("TotalYieldKg"),
      visibleFor: ["YIELD"],
      input: (
        <TextInput
          required
          value={data?.quantity as string}
          setValue={(e) => {
            const quantity = e;
            const income =
              quantity && data?.pricePerUnit
                ? String(Number(quantity) * Number(data.pricePerUnit))
                : data?.income;
            setData({ ...data, quantity: e, income });
          }}
        />
      ),
    },
    {
      label: t("PricePerUnitDin"),
      visibleFor: ["YIELD"],
      input: (
        <TextInput
          value={data?.pricePerUnit}
          setValue={(e) => {
            const pricePerUnit = e;
            const income =
              data?.quantity && pricePerUnit
                ? String(Number(data.quantity) * Number(pricePerUnit))
                : data?.income;
            setData({ ...data, pricePerUnit: e, income });
          }}
        />
      ),
    },
    {
      label: t("IncomeDescription"),
      visibleFor: ["ANOTHER_INCOME_ENTRY"],
      input: (
        <TextInput
          value={data?.description || ""}
          setValue={(e) => setData({ ...data, description: e })}
        />
      ),
    },
    {
      label: t("TotalIncomeDin"),
      visibleFor: ["YIELD", "ANOTHER_INCOME_ENTRY"],
      input: (
        <TextInput
          value={data?.income as string}
          setValue={(e) => {
            const income = e;
            const pricePerUnit =
              data?.quantity && income
                ? String(Number(income) / Number(data.quantity))
                : data?.pricePerUnit;
            setData({ ...data, income: e, pricePerUnit });
          }}
        />
      ),
    },
  ];

  const formInfo = allFields.filter(
    (f) => !f.visibleFor || f.visibleFor.includes(data?.type!)
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("AddEntry")}
      confirmButton={{
        onFormSubmit: onSubmit,
        label: t("Confirm"),
      }}
      cancelButton={{ onClick: onClose }}
      deleteButton={
        !!data?.uuid && onDelete
          ? {
              onClick: () => onDelete(data.uuid!),
              label: t("Remove"),
            }
          : undefined
      }
      size="m"
    >
      <div className="flex column gap-4">
        {formInfo.map((e) => (
          <Input key={e.label} component={e} edit />
        ))}
      </div>
    </Dialog>
  );
};
